import React, { useState } from 'react';
import { Layout, Menu, Tag, Space, Typography, Badge, Drawer, Button, Grid, Tooltip } from 'antd';
import { 
  LogoutOutlined, 
  DashboardOutlined, 
  FileTextOutlined,
  CreditCardOutlined,
  SettingOutlined,
  ShoppingOutlined,
  TeamOutlined,
  MenuOutlined,
  BarChartOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import Footer from './Footer';
import { userService, companiesService, efirmaService } from '../../services/api';

import logoMenu from '../../assets/6.png';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const UserLayout = () => {
  const { user, logout, updateUserBalance } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [taxCalcEnabled, setTaxCalcEnabled] = useState(false);
  const [taxCalcReason, setTaxCalcReason] = useState('Para acceder a esta sección debes cargar tu FIEL');

  // Refrescar balance al cambiar de ruta
  React.useEffect(() => {
    let isMounted = true;

    const refreshBalance = async () => {
      try {
        const data = await userService.getBalance();
        if (isMounted && data) {
          updateUserBalance(data);
        }
      } catch (error) {
        console.error('Error actualizando balance de créditos:', error);
      }
    };

    // Solo intentar refrescar si hay usuario autenticado
    if (user) {
      refreshBalance();
    }

    return () => {
      isMounted = false;
    };
  }, [location.pathname]); // se ejecuta en cada cambio de ruta

  React.useEffect(() => {
    let isMounted = true;

    const isResico = (regimen) => {
      const r = String(regimen || '').trim().toUpperCase();
      return r === 'RESICO' || r === '626';
    };

    const checkTaxCalcEligibility = async () => {
      if (!user) return;
      try {
        const resp = await companiesService.getCompanies();
        const list = resp?.data?.companies || [];
        const resicoCompanies = (list || []).filter((c) => isResico(c?.fiscal_regimen));

        if (resicoCompanies.length === 0) {
          if (isMounted) {
            setTaxCalcEnabled(false);
            setTaxCalcReason('Para acceder a esta sección necesitas una empresa con régimen RESICO (626)');
          }
          return;
        }

        const companyId = resicoCompanies[0].id;
        const efirmaResp = await efirmaService.getEfirma(companyId);
        const records = efirmaResp?.data?.records || [];
        const hasEfirma = Array.isArray(records) && records.some((r) => r.active);

        if (isMounted) {
          setTaxCalcEnabled(hasEfirma);
          setTaxCalcReason('Para acceder a esta sección debes cargar tu FIEL en Configuración → eFirma');
        }
      } catch (e) {
        if (isMounted) {
          setTaxCalcEnabled(false);
          setTaxCalcReason('Para acceder a esta sección debes cargar tu FIEL en Configuración → eFirma');
        }
      }
    };

    checkTaxCalcEligibility();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Obtener información de créditos del balance incluido en el login
  const balance = user?.balance || {};
  const totalCreditsAvailable = balance.total_credits_available || 0;
  const needsRenewal = balance.needs_renewal || false;

  const allowedScreens = Array.isArray(user?.allowed_screens) ? user.allowed_screens : null;
  const hasScreenAccess = (screenKey) => {
    if (screenKey === 'dashboard') return true;
    return !allowedScreens || allowedScreens.includes(screenKey);
  };

  // Determinar el color del badge según la disponibilidad de créditos
  const getCreditsBadgeColor = () => {
    if (totalCreditsAvailable === 0) return 'red';
    if (totalCreditsAvailable <= 5) return 'orange';
    if (needsRenewal) return '#D4AC0D';
    return 'green';
  };

  const menuItems = [
    hasScreenAccess('dashboard') && {
      key: '/dashboard',
      icon: <DashboardOutlined style={{ color: '#333333' }} />,
      label: 'Dashboard',
      onClick: () => {
        navigate('/dashboard');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('tax-calculation') && {
      key: '/tax-calculation',
      icon: <BarChartOutlined style={{ color: taxCalcEnabled ? '#333333' : '#bfbfbf' }} />,
      label: taxCalcEnabled ? (
        'Cálculo de impuestos'
      ) : (
        <Tooltip title={taxCalcReason} placement="right">
          <span style={{ color: '#bfbfbf' }}>Cálculo de impuestos</span>
        </Tooltip>
      ),
      disabled: !taxCalcEnabled,
      onClick: () => {
        if (!taxCalcEnabled) return;
        navigate('/tax-calculation');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('invoices') && {
      key: '/invoices',
      icon: <FileTextOutlined style={{ color: '#333333' }} />,
      label: 'Facturas',
      onClick: () => {
        navigate('/invoices');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('cobranza-ppd') && {
      key: '/cobranza/ppd',
      icon: <CreditCardOutlined style={{ color: '#333333' }} />,
      label: 'Cobranza',
      onClick: () => {
        navigate('/cobranza/ppd');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('nominas') && {
      key: '/nominas',
      icon: <FileTextOutlined style={{ color: '#333333' }} />,
      label: 'Nóminas',
      onClick: () => {
        navigate('/nominas');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('products') && {
      key: '/products',
      icon: <ShoppingOutlined style={{ color: '#333333' }} />,
      label: 'Productos/Servicios',
      onClick: () => {
        navigate('/products');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('clients') && {
      key: '/clients',
      icon: <TeamOutlined style={{ color: '#333333' }} />,
      label: 'Clientes',
      onClick: () => {
        navigate('/clients');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('employees') && {
      key: '/employees',
      icon: <TeamOutlined style={{ color: '#333333' }} />,
      label: 'Empleados',
      onClick: () => {
        navigate('/employees');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('settings') && {
      key: '/settings',
      icon: <SettingOutlined style={{ color: '#333333' }} />,
      label: 'Configuración',
      onClick: () => {
        navigate('/settings');
        setDrawerVisible(false);
      },
    },
    hasScreenAccess('autofactura-configs') && {
      key: '/autofactura-configs',
      icon: <LinkOutlined style={{ color: '#333333' }} />,
      label: 'Autoportal',
      onClick: () => {
        navigate('/autofactura-configs');
        setDrawerVisible(false);
      },
    },
    {
      key: 'logout',
      icon: <LogoutOutlined style={{ color: '#333333' }} />,
      label: 'Salir',
      onClick: handleLogout,
    },
  ].filter(Boolean);

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          padding: screens.lg ? '0 24px' : '0 12px',
          height: screens.lg ? '64px' : '50px',
          lineHeight: screens.lg ? '64px' : '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'Darker Grotesque',
          fontWeight: 700,
          color: '#333333',
          width: '100%',
          backgroundColor: '#ffffff',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
      >
        {/* Logo */}
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            height: '100%'
          }} 
          onClick={() => navigate('/dashboard')}
        >
          <img 
            src={logoMenu} 
            alt="Mi Factura" 
            style={{ height: screens.lg ? '40px' : '28px' }} 
          />
        </div>

        <div style={{ flex: 1 }} />

        {/* Información de créditos y menú móvil */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: screens.lg ? '16px' : '12px', 
          flexShrink: 0,
        }}>
          {/* Mostrar créditos disponibles */}
          <Space 
            align="center" 
            style={{ cursor: hasScreenAccess('credits') ? 'pointer' : 'not-allowed' }} 
            onClick={() => {
              if (!hasScreenAccess('credits')) return;
              navigate('/credits');
            }}
          >
            <CreditCardOutlined style={{ color: '#333333', fontSize: '16px' }} />
            <Badge 
              count={totalCreditsAvailable} 
              showZero 
              color={getCreditsBadgeColor()}
              style={{ 
                backgroundColor: getCreditsBadgeColor(),
                color: 'white',
                fontWeight: 'bold',
              }}
            />
            {screens.lg && (
              <Text style={{ color: '#333333', fontSize: '14px' }}>
                Créditos
              </Text>
            )}
            {needsRenewal && (
              <Tag color="#D4AC0D" size="small">
                {screens.lg ? 'Plan Vencido' : 'Vencido'}
              </Tag>
            )}
          </Space>

          {/* Botón menú móvil */}
          {!screens.lg && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: '20px' }} />}
              onClick={() => setDrawerVisible(true)}
            />
          )}
        </div>
      </Header>

      {/* Drawer para menú móvil */}
      <Drawer
        title="Menú"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={250}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{ border: 'none' }}
        />
      </Drawer>

      <Layout style={{ flex: 1 }}>
        {screens.lg && (
          <Sider
            width={240}
            theme="light"
            style={{
              backgroundColor: '#ffffff',
              borderRight: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              style={{ border: 'none', height: '100%' }}
            />
          </Sider>
        )}

        <Content
          style={{
            padding: screens.lg ? '24px' : '12px',
            backgroundColor: '#f0f2f5',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <div style={{ flex: 1 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
      
      <Footer />
    </Layout>
  );
};

export default UserLayout;
